"use client";

import { Diff, Minus, Plus } from "lucide-react";
import * as React from "react";
import { BudgetEntry, PublicBudgetData } from "./types";
import { useTranslations } from "use-intl";
interface BudgetViewTableProps {
  budgetData: PublicBudgetData;
  comparedBudgetData?: PublicBudgetData;
}
const BudgetViewTable: React.FC<BudgetViewTableProps> = ({
  budgetData,
  comparedBudgetData
}) => {
  const t = useTranslations("budget");
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("ro-RO", {
      style: "currency",
      currency: "RON"
    }).format(value);
  };
  const entryCodeMap = new Map<string, [BudgetEntry | null, BudgetEntry | null]>();
  budgetData.budgetEntries.forEach(entry => {
    if (!comparedBudgetData) {
      entryCodeMap.set(entry.code!, [entry, null]);
    } else {
      const comparedEntry = comparedBudgetData.budgetEntries.find(e => e.code === entry.code);
      entryCodeMap.set(entry.code!, [entry, comparedEntry || null]);
    }
  });
  if (comparedBudgetData) {
    comparedBudgetData.budgetEntries.forEach(entry => {
      if (!budgetData.budgetEntries.find(e => e.code === entry.code)) {
        entryCodeMap.set(entry.code!, [null, entry]);
      }
    });
  }
  const entries = Array.from(entryCodeMap.values());
  return <div className="overflow-x-auto rounded-xl border border-glow/20" data-sentry-component="BudgetViewTable" data-sentry-source-file="BudgetViewTable.tsx">
      <table className="min-w-full leading-normal">
        <thead>
          <tr className="border-b-2 border-glow/30 bg-glow/10 text-left text-xs font-semibold uppercase tracking-wider text-primary/90">
            {!!comparedBudgetData && <th className="py-4 pl-5"></th>}
            <th className="px-5 py-4">{t("code")}</th>
            <th className="px-5 py-4">{t("name")}</th>
            <th className="px-5 py-4">{t("q1")}</th>
            <th className="px-5 py-4">{t("q2")}</th>
            <th className="px-5 py-4">{t("q3")}</th>
            <th className="px-5 py-4">{t("q4")}</th>
            <th className="px-5 py-4">{t("remaining")}</th>
          </tr>
        </thead>
        <tbody>
          {entries.map(([entry, comparedEntry], entryIndex) => {
          const isLast = entryIndex === budgetData.budgetEntries.length - 1;
          const isNameDifferent = comparedEntry && entry?.displayName !== comparedEntry.displayName;
          const isQ1Different = comparedEntry && entry?.firstTrimester !== comparedEntry.firstTrimester;
          const isQ2Different = comparedEntry && entry?.secondTrimester !== comparedEntry.secondTrimester;
          const isQ3Different = comparedEntry && entry?.thirdTrimester !== comparedEntry.thirdTrimester;
          const isQ4Different = comparedEntry && entry?.fourthTrimester !== comparedEntry.fourthTrimester;
          const isRemainingDifferent = comparedEntry && entry?.unpaid !== comparedEntry.unpaid;
          const hasAtLeastOneDifference = [isNameDifferent, isQ1Different, isQ2Different, isQ3Different, isQ4Different, isRemainingDifferent].some(isDifferent => isDifferent);
          const isLineNew = !comparedEntry && comparedBudgetData !== undefined;
          const isLineDeleted = !entry && comparedBudgetData !== undefined;
          return <tr key={entryIndex} className={`border-glow/20 bg-background p-5 text-sm ${isLast ? "" : "border-b"} ${hasAtLeastOneDifference && !isLineNew && !isLineDeleted ? "bg-yellow-500/10" : ""} ${isLineNew ? "bg-green-500/10 text-green-700" : ""} ${isLineDeleted ? "bg-red-500/10 text-red-500 line-through" : ""} ${!isLineNew && !isLineDeleted && !hasAtLeastOneDifference ? "text-primary/90" : ""}
                `}>
                {!!comparedBudgetData && <td className="py-4 pl-5">
                    {isLineNew && <Plus size={16} className="text-green-700" />}
                    {isLineDeleted && <Minus size={16} className="text-red-700" />}
                    {!isLineNew && !isLineDeleted && hasAtLeastOneDifference && <Diff size={16} className="text-yellow-700" />}
                  </td>}
                <td className="px-5 py-4">{entry?.code || comparedEntry?.code}</td>
                <td className="px-5 py-4">
                  {isNameDifferent && <div className="flex gap-1">
                      <span className="font-normal text-green-700">{entry?.displayName}</span>
                      <span className="font-light text-red-500 line-through">
                        {comparedEntry?.displayName}
                      </span>
                    </div>}
                  {!isNameDifferent && (entry?.displayName || comparedEntry?.displayName)}
                </td>
                <td className="px-5 py-4">
                  {isQ1Different && <div className="flex gap-1">
                      {entry?.firstTrimester && <span className="font-normal text-green-700">
                          {formatCurrency(entry?.firstTrimester)}
                        </span>}
                      {comparedEntry?.firstTrimester && <span className="font-light text-red-500 line-through">
                          {formatCurrency(comparedEntry?.firstTrimester)}
                        </span>}
                    </div>}

                  {!isQ1Different && entry?.firstTrimester && <span className={entry?.firstTrimester < 0 ? "text-red-500" : "text-green-700"}>
                      {formatCurrency(entry?.firstTrimester)}
                    </span>}
                </td>
                <td className="px-5 py-4">
                  {isQ2Different && <div className="flex gap-1">
                      {entry?.secondTrimester && <span className="font-normal text-green-700">
                          {formatCurrency(entry?.secondTrimester)}
                        </span>}
                      {comparedEntry?.secondTrimester && <span className="font-light text-red-500 line-through">
                          {formatCurrency(comparedEntry?.secondTrimester)}
                        </span>}
                    </div>}
                  {!isQ2Different && entry?.secondTrimester && <span className={entry?.secondTrimester < 0 ? "text-red-500" : "text-green-700"}>
                      {formatCurrency(entry?.secondTrimester)}
                    </span>}
                </td>
                <td className="px-5 py-4">
                  {isQ3Different && <div className="flex gap-1">
                      {entry?.thirdTrimester && <span className="font-normal text-green-700">
                          {formatCurrency(entry?.thirdTrimester)}
                        </span>}
                      {comparedEntry?.thirdTrimester && <span className="font-light text-red-500 line-through">
                          {formatCurrency(comparedEntry?.thirdTrimester)}
                        </span>}
                    </div>}
                  {!isQ3Different && entry?.thirdTrimester && <span className={entry?.thirdTrimester < 0 ? "text-red-500" : "text-green-700"}>
                      {formatCurrency(entry?.thirdTrimester)}
                    </span>}
                </td>
                <td className="px-5 py-4">
                  {isQ4Different && <div className="flex gap-1">
                      {entry?.fourthTrimester && <span className="font-normal text-green-700">
                          {formatCurrency(entry?.fourthTrimester)}
                        </span>}
                      {comparedEntry?.fourthTrimester && <span className="font-light text-red-500 line-through">
                          {formatCurrency(comparedEntry?.fourthTrimester)}
                        </span>}
                    </div>}
                  {!isQ4Different && entry?.fourthTrimester && <span className={entry?.fourthTrimester < 0 ? "text-red-500" : "text-green-700"}>
                      {formatCurrency(entry?.fourthTrimester)}
                    </span>}
                </td>
                <td className="px-5 py-4">
                  {isRemainingDifferent && <div className="flex gap-1">
                      {entry?.unpaid && <span className="font-normal text-green-700">
                          {formatCurrency(entry?.unpaid)}
                        </span>}
                      {comparedEntry?.unpaid && <span className="font-light text-red-500 line-through">
                          {formatCurrency(comparedEntry?.unpaid)}
                        </span>}
                    </div>}
                  {!isRemainingDifferent && <span className={(entry?.unpaid || 0) < 0 ? "text-red-500" : "text-green-700"}>
                      {formatCurrency(entry?.unpaid || 0)}
                    </span>}
                </td>
              </tr>;
        })}
        </tbody>
      </table>
    </div>;
};
export default BudgetViewTable;